import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withRouter, Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { NotificationIcon, MenuIcon, EnvelopeOpenIcon } from 'components/icons';
import { Container, Spacing } from 'components/Layout';
import { Button } from 'components/Form';
import Avatar from 'components/Avatar';
import Search from 'components/Search';
import HeaderDropDowns from './HeaderDropDowns';
import {NOTIFICATIONS} from 'routes'

import { useClickOutside } from 'hooks/useClickOutside';

import { useStore } from 'store';

import { HEADER_HEIGHT } from 'constants/Layout';
import SiteInfo from 'constants/SiteInfo.json';

import * as Routes from 'routes';

const Root = styled(Container)`
  position: sticky;
  top: 0;
  background-color: white;
  z-index: ${(p) => p.theme.zIndex.md};
  height: ${HEADER_HEIGHT}px;
  border-bottom: 1px solid ${(p) => (p.theme.colors.border.light)};
  @media (min-width: ${(p) => p.theme.screen.md}) {
    z-index: ${(p) => p.theme.zIndex.md};
  }
`;

const Image = styled.img`
  width: auto;
  height: 60px;
  max-height: 60px;
  object-fit: contain;
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: ${HEADER_HEIGHT}px;
  margin: 0 auto;
  width: 100%;

`;

const LeftSide = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Hamburger = styled.div`
  cursor: pointer;

  @media (min-width: ${(p) => p.theme.screen.md}) {
    display: none;
  }
`;

const Logo = styled(Link)`
  display: none;
  color: ${(p) => p.theme.colors.primary.main};
  font-weight: ${(p) => p.theme.font.weight.bold};
  font-size: ${(p) => p.theme.font.size.sm};

  &:hover {
    color: ${(p) => p.theme.colors.primary.main};
  }

  @media (min-width: ${(p) => p.theme.screen.md}) {
    display: block;
  }
`;

const RightSide = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
`;

const countCSS = css`
  right: -10px;
  top: -10px;
  position: absolute;
  height: 22px;
  width: 22px;
  padding: 2px;
  letter-spacing: -1px;
  border-radius: 50%;
  color: ${(p) => p.theme.colors.white};
  background-color: ${(p) => p.theme.colors.error.main};
  font-size: ${(p) => p.theme.font.size.xxs};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const NotificationCount = styled.span`
  ${countCSS};
 
`;

const MessageCount = styled.span`
  ${countCSS};
`;

const UserProfileBtn = styled.div`
  border: 1px solid rgba(0,0,0,.15);
  border-radius: 20px;
  padding: 0 0 0 5px;
  button{
    font-weight: 100;
  }

  @media (max-width: ${(p) => p.theme.screen.md}) {
    border: none;
  }

`

const FullName = styled.p`
  margin:0;
  margin-right: 10px;

  @media (max-width: ${(p) => p.theme.screen.md}) {
   display:none;
}
`

/**
 * Header of the App when user is authenticated
 */
const Header = ({ location, toggleSideBar }) => {
  const history = useHistory()
  const [{ auth }] = useStore();
  const { t } = useTranslation()

  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [dropdownData, setDropdownData] = useState([]);

  const messageRef = useRef(null);
  const notificationRef = useRef(null);
  const userRef = useRef(null);

  const closeOnClickOutside = () => {
    if (dropdownOpen) {
      closeDropDown();
    }
  };

  useClickOutside(messageRef, closeOnClickOutside);
  useClickOutside(notificationRef, closeOnClickOutside);
  useClickOutside(userRef, closeOnClickOutside);

  const closeDropDown = () => {
    setDropdownOpen(null);
    setDropdownData([]);
  };

  useEffect(() => {
    return () => closeDropDown();
  }, [location.pathname]);

  const handleIconClick = (dropdownType) => {
    if (dropdownOpen) {
      closeDropDown();
    } else {
      if (dropdownType === 'NOTIFICATION') {
        setDropdownData(auth.user.newNotifications);
      } else if (dropdownType === 'MESSAGE') {
        setDropdownData(auth.user.newConversations);
      }

      setDropdownOpen(dropdownType);
    }
  };

  return (
    <Root>
      <Wrapper>
        <LeftSide>
          <Hamburger onClick={toggleSideBar}>
            <MenuIcon />
          </Hamburger>

          <Logo to={Routes.HOME}>
            <Image src={SiteInfo.logo} alt="logo" />
          </Logo>

          <Spacing left="sm" right="md">
            <Search location={location} placeholder={t('search')} shadow/>
          </Spacing>
        </LeftSide>

        <RightSide>
          <Spacing right="md">
            <Button ghost onClick={() => handleIconClick('MESSAGE')} style={{position: 'relative'}}>
              {auth.user.newConversations.length > 0 && (
                <MessageCount>{auth.user.newConversations.length}</MessageCount>
              )}

              <EnvelopeOpenIcon />
            </Button>
          </Spacing>

          <Spacing right="md">
            <Button ghost onClick={() => history.push(NOTIFICATIONS)}  style={{position: 'relative'}}>
              {auth.user.newNotifications.length > 0 && (
                <NotificationCount>{auth.user.newNotifications.length}</NotificationCount>
              )}
              <NotificationIcon />
            </Button>
          </Spacing>

          <UserProfileBtn>
          <Button ghost onClick={() => handleIconClick('USER')}>
            <FullName>{ auth.user.fullName}</FullName> <Avatar image={auth.user.image} isAdmin={ auth.user.isAdmin}/>
          </Button>
          </UserProfileBtn>
        </RightSide>

        <HeaderDropDowns
          messageRef={messageRef}
          notificationRef={notificationRef}
          userRef={userRef}
          dropdownOpen={dropdownOpen}
          dropdownData={dropdownData}
          closeDropDown={closeDropDown}
        />
      </Wrapper>
    </Root>
  );
};

Header.propTypes = {
  location: PropTypes.object.isRequired,
  toggleSideBar: PropTypes.func.isRequired,
};

export default withRouter(Header);
